import { Component } from 'react'
import PropTypes from 'prop-types'
import DisconnectButton from '../../components/DisconnectButton'
import ReactMarkdown from 'react-markdown'
import { uninstallInstalledApp, fetchAppScopes, fetchApp, authenticatePardot} from 'app/modules/integrations/api'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { actions as TrackingActions } from 'app/modules/tracking'
import withNotifications from 'components/shared/notifications'
import ConditionalRender from 'components/shared/ConditionalRender'
import ExternalIntegrationInstallModal from '../../ExternalIntegrationInstallModal'
import CircleOfWaiting from 'components/shared/CircleOfWaiting'
import EloquaIntegrationPage from './../../pages/EloquaIntegrationPage'
import DisconnectConfirmModal from '../../components/DisconnectConfirmModal'
import PardotIntegrationPage from './../../pages/PardotIntegrationPage'
import * as OrgSelectors from 'app/modules/org/selectors'
import { isEmpty } from 'ramda'
import './style.styl'
import { ELOQUA_APP_ID, PARDOT_APP_ID } from 'constants/ResourcesConstants'
import {ToolTip, Icon, Button} from '@driftt/tide-core-legacy'

const INSTALL_STATUS_POLL_LIMIT = 6
const CONNECTED_TEXT = `You are connected to this integration!`

class ExternalIntegration extends Component {

  static defaultProps = {
    description: ''
  }

  static contextTypes = {
    router: PropTypes.object.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      isDisconnecting: false,
      isInstallModalOpen: false,
      isFetchingScopes: false,
      scopes: [],
      isInstalling: false,
      isInstalled: props.data && props.data.installed,
      installStatusPollCount: 0,
      pollCountLimitReached: false,
      showDisconnectModal: false,
      isConnectDisabled: false,
      hasSalesforceSSO: false
    }
  }

  componentDidMount(){
    switch(this.props.data.name){
      case "Pardot":
        this.setState({
        isConnectDisabled: true
        })
        return
      default:
        return
    }
  }

  onCancelConnectIntegration = () => {
    this.setState({
      isInstallModalOpen: false
     })
  }

  disconnectIntegration = () => {
    const { data, actions } = this.props
    
    const onDone = () => {
      actions.track('Integration Disconnected', { integration: data.name, connected: false })
      window.location.href = `${window.location.origin}/settings2/integrations`
    }

    const onError = () => {
      this.props.notifications.error('Ooops, something went wrong. Plese try again.')
    }

    const onStateSet = () => uninstallInstalledApp(this.props.data.id)
      .then(onDone)
      .catch(onError)

    this.setState({ isDisconnecting: true }, onStateSet)
  }

  closeModal = () => {
    this.setState({ isInstallModalOpen: false })
  }

  handleClickSSOInstall = () => {
    // reload page because auth has been done for both sso and dev plat on the backend
      location.reload()
  }

  onInstallClick = () => {
    const { isFetchingScopes, scopes } = this.state
    const { data, notifications } = this.props

    if (isFetchingScopes) {
      return
    }

    const onScopesFetched = (scopes) => {
      this.setState({
        scopes,
        isInstallModalOpen: true,
        isFetchingScopes: false
      })
    }

    const onError = (text = 'Ooops, something went wrong. Plese try again.') => {
      notifications.error(text)
      this.setState({ isFetchingScopes: false })
    }

    const fetchScopes = () => fetchAppScopes(data.id)
      .then(onScopesFetched)
      .catch(onError)

    const integrationWillInstall = () => {
      if (data.id !== PARDOT_APP_ID) {
        fetchScopes()
      }
    }

    if (isEmpty(scopes)) {
      this.setState({ isFetchingScopes: true }, integrationWillInstall)
    } else {
      this.setState({ isInstallModalOpen: true })
    }
  }

  componentWillUnmount() {
    this.stopPollingForInstallStatus()
  }

  pollForInstallStatus = () => {
    const { isInstalling, installStatusPollCount } = this.state
    const { data } = this.props

    const onDone = ({ installed }) => {
      if (installed) {
        this.setState({
          isInstalled: true,
          isInstalling: false
        })
      }
    }

    fetchApp(data.id).then(onDone)

    if (isInstalling && installStatusPollCount < INSTALL_STATUS_POLL_LIMIT) {
      const pollingTimeout = setTimeout(this.pollForInstallStatus, 5000)
      this.setState({
        pollingTimeout,
        installStatusPollCount: installStatusPollCount + 1
      })
    } else if (isInstalling && installStatusPollCount >= INSTALL_STATUS_POLL_LIMIT) {
      this.setState({
        isInstalling: false,
        pollCountLimitReached: true
      })
    }
  }

  stopPollingForInstallStatus = () => {
    const { pollingTimeout } = this.state

    if (pollingTimeout) {
      clearTimeout(pollingTimeout)
    }
  }

  onAuthorized = () => {
    const { data, actions } = this.props
    actions.track('Integration Connected', { integration: data.name, connected: true })
    const onStateSet = () => this.pollForInstallStatus()
    this.setState({
      isInstallModalOpen: false,
      isInstalling: true,
      installStatusPollCount: 1,
      pollCountLimitReached: false
    }, onStateSet)
  }

  renderIntegrationContent(data) {
    const appId = data.id

    // Render either default third party app page or use a custom component.
    switch (appId) {
      case ELOQUA_APP_ID:
        return <EloquaIntegrationPage data={data}/>
      case PARDOT_APP_ID:
        return <PardotIntegrationPage
                  data={data}
                  onClickSSO={this.handleClickSSOInstall}
                  />
      default:
        return <ReactMarkdown source={data.longDescription} />
    }
  }
  showDisconnectModal = () => {
    this.setState({
      showDisconnectModal: true
    })
  }
  hideDisconnectModal = () =>
  {
    this.setState({
      showDisconnectModal: false
    })
  }

  render() {
    const { data } = this.props
    const externalUrl = data.externalUrl
    const externalUrlEnabled = !!data.externalUrlEnabled && externalUrl

    const {
      isDisconnecting,
      isInstallModalOpen,
      isFetchingScopes,
      scopes,
      isInstalled,
      isInstalling,
      pollCountLimitReached,
      isConnectDisabled
    } = this.state
    return (
      <div className="app-integration-page">
        <div className="app-integration-page-description">
          {this.renderIntegrationContent(data)}
        </div>

        <ConditionalRender condition={isInstalled}>
        <div className="common-integration-page-connection-indicator">
          <ToolTip className="common-integratin-page-connection-indicator-text" component={CONNECTED_TEXT} flow='down'>
            <Icon name="check-circle" />
            <span>Connected!</span>
          </ToolTip>
        </div>

        </ConditionalRender>

        <ConditionalRender condition={data.distributionStatus === 'INTERNAL'}>
          <div className="app-integration-page-private">
            <span className="app-integration-page-private-label">
              Private
            </span>
            <p className="app-integration-page-private-description">
              This integration is currently only available to your Drift organization.
            </p>
          </div>
        </ConditionalRender>

        <ConditionalRender condition={isInstalled}>
          <DisconnectButton
            onClick={this.showDisconnectModal}
            isProcessing={isDisconnecting}
          />
        </ConditionalRender>

        <ConditionalRender condition={pollCountLimitReached}>
          <div className="app-integration-page-timeout">
            <span className="app-integration-page-timeout-label">
              Timeout
            </span>
            <p className="app-integration-page-timeout-description">
              Hmm, it looks like this app is experiencing some issues at the moment. Please try again.
            </p>
          </div>
        </ConditionalRender>

        <ConditionalRender condition={!externalUrlEnabled && data.id !== PARDOT_APP_ID}>

          <ConditionalRender condition={isInstalling}>
            <button className="drift-button drift-button-primary app-integration-page-install-button">
              Connecting...
            </button>
          </ConditionalRender>

          <ConditionalRender condition={!isInstalled && !isInstalling}>
            <Button
              onClick={this.onInstallClick}
              className="drift-button app-integration-page-install-button"
              disabled={isConnectDisabled}
              >
              <ConditionalRender condition={!isFetchingScopes}>
                Connect
              </ConditionalRender>
              <ConditionalRender condition={isFetchingScopes}>
                <CircleOfWaiting />
              </ConditionalRender>
            </Button>
          </ConditionalRender>
        </ConditionalRender>

        <ConditionalRender condition={externalUrlEnabled}>
          <a href={externalUrl} target="_blank">
            <button className="drift-button drift-button-primary app-integration-page-install-button">
              Go to Integration Page
            </button>
          </a>
          <p>This button will open a new tab.</p>
        </ConditionalRender>

        <ExternalIntegrationInstallModal
          isOpen={isInstallModalOpen}
          closeModal={this.closeModal}
          appId={data.id}
          appName={data.name}
          appIconSrc={data.iconUrl}
          scopes={scopes}
          onAuthorized={this.onAuthorized}
        />

        <DisconnectConfirmModal
          integrationName={data.name}
          onDisconnect={this.disconnectIntegration}
          onClose={this.hideDisconnectModal}
          open={this.state.showDisconnectModal}
          />
      </div>
    )
  }
}

const mapStateToProps = (state) => ({
  orgId: OrgSelectors.orgId(state)
})

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators({
      track: TrackingActions.track,
    }, dispatch)
  }
}

export default withNotifications(connect(
  mapStateToProps,
  mapDispatchToProps
)(ExternalIntegration))



// WEBPACK FOOTER //
// ./src/scripts/components/Integration/services/ExternalIntegration/index.js